// Auth context + hook. Wraps keycloak-js so components consume `useAuth()`
// instead of touching the KC singleton. On mount we run `check-sso` — if the
// user has an existing session we get a token; otherwise `authed` stays false
// and the caller decides whether to redirect to login.

import Keycloak, { type KeycloakInstance } from 'keycloak-js';
import {
    createContext, useContext, useEffect, useMemo, useState,
    type PropsWithChildren,
} from 'react';

interface AuthCtx {
    kc: KeycloakInstance | null;
    authed: boolean;
    login:  () => void;
    logout: () => void;
    /** JWT for the current session; falsy while unauthenticated or during refresh. */
    token:  () => string | undefined;
    /** Best-effort refresh of the JWT; safe to call before every fetch. */
    ensureFresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

// One Keycloak instance per page load. Instantiating twice throws in dev mode
// (StrictMode double-effects), so we guard with a module-level flag.
let kcSingleton: KeycloakInstance | null = null;
let kcInitPromise: Promise<boolean> | null = null;

// Runtime config fetched from /config.json before KC init. Lets one JS bundle
// work across localhost, prod, custom domains — no compile-time env vars in
// the Vite build. Cached module-scoped so we only fetch once per page load.
interface RuntimeConfig {
    kc_url:    string;
    kc_realm:  string;
    kc_client: string;
    host:      string;
}
let cachedConfig: RuntimeConfig | null = null;
async function loadConfig(): Promise<RuntimeConfig> {
    if (cachedConfig) return cachedConfig;
    try {
        const r = await fetch('/config.json', { cache: 'no-cache' });
        if (r.ok) {
            cachedConfig = await r.json();
            return cachedConfig!;
        }
    } catch { /* fall through to localhost defaults */ }
    // Fallback for local dev when the server isn't serving /config.json yet.
    cachedConfig = { kc_url: 'http://localhost:8081', kc_realm: 'onhere', kc_client: 'onhere-web', host: 'localhost' };
    return cachedConfig;
}
// One-shot redirect flag — shared by BOTH ensureFresh() and api.ts's 401
// handler so a burst of parallel failures (Bell + Paper + Profile all firing
// at once) triggers exactly one navigation, not five. Reset only by page
// reload, which is fine because kc.login() navigates away anyway.
let redirectInFlight = false;

// Loop-guard: sessionStorage record of the last N triggerLogin attempts. If
// we bounce through KC more than 2 times inside 30s, the KC SSO session is
// almost certainly dead-but-not-empty — auto-redirect will loop forever.
// In that case we stop trying and let the UI show its unauthenticated state
// with a manual "Log in" button, so the user can trigger a fresh flow when
// they choose to.
const LOOP_KEY  = 'onhere_login_attempts';
const LOOP_WIN  = 30_000;   // 30s window
const LOOP_MAX  = 2;        // >2 attempts in the window = loop detected

/** True when we've bounced through login too many times too fast. */
export function loginLoopDetected(): boolean {
    try {
        const raw = sessionStorage.getItem(LOOP_KEY);
        if (!raw) return false;
        const attempts: number[] = JSON.parse(raw);
        const cutoff = Date.now() - LOOP_WIN;
        return attempts.filter(t => t > cutoff).length > LOOP_MAX;
    } catch { return false; }
}

/** Record that we're about to bounce through KC. Callers check first. */
function recordLoginAttempt() {
    try {
        const cutoff = Date.now() - LOOP_WIN;
        const raw = sessionStorage.getItem(LOOP_KEY);
        const prior: number[] = raw ? JSON.parse(raw) : [];
        const kept = prior.filter(t => t > cutoff);
        kept.push(Date.now());
        sessionStorage.setItem(LOOP_KEY, JSON.stringify(kept));
    } catch { /* private-mode etc — best-effort */ }
}

/** Called after a successful init — clears the loop record so a normal
 *  session doesn't accumulate stale attempts. */
export function clearLoginLoopRecord() {
    try { sessionStorage.removeItem(LOOP_KEY); } catch { /* ignore */ }
}

async function getKC(): Promise<KeycloakInstance> {
    if (!kcSingleton) {
        const cfg = await loadConfig();
        kcSingleton = new Keycloak({
            url:      cfg.kc_url,
            realm:    cfg.kc_realm,
            clientId: cfg.kc_client,
        });
    }
    return kcSingleton;
}

/**
 * triggerLogin bounces to Keycloak, preserving the current URL for return.
 * Idempotent per page-load — call it as many times as you want, it navigates
 * once. Exported so api.ts can share the flag (no infinite refresh loops).
 *
 * Also guards against KC-SSO-dead loops: if we've bounced through KC more
 * than LOOP_MAX times inside LOOP_WIN, we STOP triggering redirects and let
 * the caller / UI decide what to do. That surface as "Log in" buttons on
 * the affected pages instead of a browser lockup.
 */
export function triggerLogin(kc: KeycloakInstance) {
    if (redirectInFlight) return;
    if (loginLoopDetected()) {
        // Something's wrong (KC SSO session expired but cookie sticks around,
        // clock drift, etc.). Log noisily and let the UI take over.
        console.warn('login loop detected — stopping auto-redirect. Click "Log in" to retry.');
        return;
    }
    redirectInFlight = true;
    recordLoginAttempt();
    // Strip any leftover KC callback params from the URL so the redirect_uri
    // doesn't include a stale &code=&state= that KC might reject on the way
    // back and cascade into another loop.
    const clean = new URL(window.location.href);
    ['code', 'state', 'session_state', 'iss'].forEach(k => clean.searchParams.delete(k));
    try {
        kc.login({ redirectUri: clean.toString() });
    } catch (e) {
        // kc.init rejected or ran with a broken adapter — the only safe
        // recovery is a full page reload, which resets every module-scope
        // singleton (kcInitPromise, kcSingleton, redirectInFlight) and
        // lets init try again from scratch.
        console.error('[auth] kc.login threw — reloading to recover', e);
        redirectInFlight = false;
        window.location.reload();
    }
}

/** True when the parsed token's exp claim is in the past (with 5s slack). */
export function isTokenExpired(parsed: { exp?: number } | undefined): boolean {
    if (!parsed || typeof parsed.exp !== 'number') return true;
    return parsed.exp * 1000 < Date.now() + 5000;
}

export function AuthProvider({ children }: PropsWithChildren) {
    // KC is loaded async because it needs the runtime config from /config.json.
    // While loading, `kc` is null and children see !authed && !ready → nothing
    // renders (same as before this refactor — no visible flash).
    const [kc, setKC] = useState<KeycloakInstance | null>(null);
    const [authed, setAuthed] = useState(false);
    const [ready,  setReady]  = useState(false);

    // Load config + KC instance.
    useEffect(() => {
        getKC().then(setKC).catch(e => {
            console.error('kc load failed', e);
            setReady(true);
        });
    }, []);

    useEffect(() => {
        if (!kc) return;
        if (!kcInitPromise) {
            // check-sso via iframe (public/silent-check-sso.html) so anon
            // visitors get silently upgraded to authed if they have an
            // active KC session — WITHOUT a full-page redirect. Full-page
            // redirects loop on cross-origin dev setups (localhost:5173 ↔
            // localhost:8081) and are ugly in general. Works identically
            // in local + prod.
            //
            // If you find yourself here debugging a KC loop again, see the
            // memory `project_auth_setup.md` — probably don't remove this
            // silentCheckSsoRedirectUri.
            kcInitPromise = kc.init({
                onLoad: 'check-sso',
                silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
                pkceMethod: 'S256',
                checkLoginIframe: false,
            });
        }
        kcInitPromise
            .then((ok) => {
                setReady(true);
                if (!ok) return;
                if (isTokenExpired(kc.tokenParsed as { exp?: number } | undefined)) {
                    triggerLogin(kc);
                    return;
                }
                clearLoginLoopRecord();
                setAuthed(true);
            })
            .catch((e) => { console.error('kc init failed', e); setReady(true); });
    }, [kc]);

    // Proactive background refresh — keeps the access token warm even when
    // the user is just reading (no API calls to trigger the per-call refresh
    // in api.ts). Fires every 5 minutes. Silent on success; a failed refresh
    // means the KC SSO session died and the next click will bounce to login
    // anyway — no need to interrupt the reader with a redirect right now.
    useEffect(() => {
        if (!authed || !kc) return;
        const id = window.setInterval(() => {
            kc.updateToken(60).catch(() => { /* silent — loop guard handles */ });
        }, 5 * 60 * 1000);
        return () => window.clearInterval(id);
    }, [kc, authed]);

    const value = useMemo<AuthCtx>(() => ({
        kc,
        authed,
        // Login click always bypasses the loop guard. Guards on kc=null so
        // the first click after page load (before /config.json resolves)
        // can't crash — extremely narrow race.
        login:  () => { if (!kc) return; clearLoginLoopRecord(); triggerLogin(kc); },
        logout: () => { if (kc) kc.logout({ redirectUri: window.location.href }); },
        token:  () => kc?.token,
        ensureFresh: async () => {
            if (!kc) return;
            // Unauth visitor — no token to refresh, no login to trigger. Just
            // let the caller proceed; the api client will either hit a
            // /api/public/* endpoint that works without a bearer, or a private
            // /api/* endpoint that 401s and the 401 handler will decide.
            if (!authed) return;
            try {
                await kc.updateToken(5);
                if (isTokenExpired(kc.tokenParsed as { exp?: number } | undefined)) {
                    triggerLogin(kc);
                }
            } catch {
                triggerLogin(kc);
            }
        },
    }), [kc, authed]);

    // Don't render children until Keycloak has decided — otherwise every page
    // flashes its logged-out state for a frame.
    if (!ready) return null;
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
    const v = useContext(Ctx);
    if (!v) throw new Error('useAuth must be inside <AuthProvider>');
    return v;
}
